// Single-select assignee picker — replaces the Claim + Reassign pair in
// the ticket-detail action row. One dropdown for the whole "who owns
// this ticket" question.
//
// UX matches the WatcherPicker introduced alongside this (same shape,
// same primitives):
//   - Self pinned at the top → one-click claim (or no-op if already
//     yours), so the muscle memory for "I'm taking this" survives.
//   - Divider, then every active teammate listed alphabetically;
//     clicking one transfers the ticket to them (reassign action).
//   - Search box for filtering as the team grows.
//   - Trigger shows the current state — "Assign", "Mine", or the
//     current assignee's avatar + first name — so you can read the
//     state without opening.
//
// Backend: uses the existing dispatcher actions via `postAction`:
//   - Self  → 'claim'   (sets assignee_slack_id to actor; flips new→in_progress)
//   - Other → 'reassign' (payload.reassign_to; notifies the new assignee via
//                         the `reassigned_to_you` notification path already in
//                         the dispatcher)
// No new server-side work needed — CLAUDE.md §5 single-source-of-action-
// logic stays intact.

import { useMemo, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/UserAvatar';
import {
  PickerCheck,
  PickerEmpty,
  PickerSearch,
  PICKER_ROW_CLASS,
} from '@/components/TeammatePickerParts';
import { useTeamMembers, useTicket, type TeamMember } from '@/lib/queries';
import { useAccess } from '@/lib/use-access';
import { useTicketAction } from '@/lib/use-ticket-action';
import { useAuth } from '@/lib/auth-guard';
import { ChevronDown, Hand, UserPlus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  ticketId: string;
  /** Current assignee's slack_user_id, or null when unassigned. Drives
   *  the trigger label + the row highlights inside the dropdown. */
  currentAssigneeSlackId: string | null;
  teamMembersBySlackId?: Map<string, TeamMember>;
  /** Below `sm`, show only the icon/avatar + chevron (the ticket toolbar
   *  is tight on phones). The full label returns from `sm` up and is
   *  always exposed to assistive tech via aria-label. */
  compact?: boolean;
}

export function AssignButton({
  ticketId,
  currentAssigneeSlackId,
  teamMembersBySlackId,
  compact = false,
}: Props) {
  const { teamMember } = useAuth();
  const mySlack = teamMember?.slack_user_id ?? null;
  const teamQ = useTeamMembers();

  // Per-inbox permissions (migration 078): claiming needs tickets.claim, and
  // taking a ticket someone else holds or giving it to somebody else needs
  // tickets.assign_others. The dispatcher enforces; this hides the dead rows.
  const access = useAccess();
  const ticket = useTicket(ticketId).data;
  const inboxId = ticket?.inbox_id ?? null;
  const canClaim = access.canIn(inboxId, 'tickets.claim');
  const canAssignOthers = access.canIn(inboxId, 'tickets.assign_others');
  const heldByOther = !!currentAssigneeSlackId && currentAssigneeSlackId !== mySlack;
  const showSelfRow = canClaim && (!heldByOther || canAssignOthers);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Claim / reassign show their result the moment they're picked and the
  // server confirms in the background (lib/use-ticket-action.ts), so the
  // trigger flips to "Mine" / the new avatar with no wait.
  const { run } = useTicketAction();

  // Active teammates only. Self pinned at the top; rest alphabetised.
  const { selfMember, others, currentMember } = useMemo(() => {
    const all = (teamQ.data ?? []).filter((m) => m.active);
    const self = mySlack ? all.find((m) => m.slack_user_id === mySlack) ?? null : null;
    const current = currentAssigneeSlackId
      ? all.find((m) => m.slack_user_id === currentAssigneeSlackId) ?? null
      : null;
    const rest = all.filter((m) => m.slack_user_id !== mySlack);
    rest.sort((a, b) =>
      (a.name ?? a.email ?? '').localeCompare(b.name ?? b.email ?? ''),
    );
    const term = search.trim().toLowerCase();
    const filtered = term
      ? rest.filter((m) =>
          `${m.name ?? ''} ${m.email ?? ''}`.toLowerCase().includes(term),
        )
      : rest;
    return { selfMember: self, others: filtered, currentMember: current };
  }, [teamQ.data, mySlack, currentAssigneeSlackId, search]);

  const pick = (targetSlackId: string) => {
    // Re-assigning to the same person is a wasted round-trip + a
    // misleading "Ticket reassigned" toast. Bail silently — same
    // intent as the keyboard-shortcut claim's self-check on
    // ticket-detail.tsx.
    if (targetSlackId === currentAssigneeSlackId) {
      setOpen(false);
      return;
    }
    const isSelf = targetSlackId === mySlack;
    setOpen(false);
    void run(
      ticketId,
      isSelf ? 'claim' : 'reassign',
      isSelf ? undefined : { reassign_to: targetSlackId },
      { subject: ticket?.subject },
    );
  };

  // Trigger label/icon reflects current state at a glance.
  const isMine = !!mySlack && currentAssigneeSlackId === mySlack;
  const isAssigned = !!currentAssigneeSlackId;
  const labelCls = compact ? 'hidden sm:inline' : undefined;
  let triggerLabel: React.ReactNode;
  let ariaLabel = 'Assign ticket';
  if (isMine) {
    ariaLabel = 'Assigned to you';
    triggerLabel = (
      <>
        <Hand className="h-3.5 w-3.5" />
        <span className={labelCls}>Mine</span>
      </>
    );
  } else if (isAssigned && currentMember) {
    const firstName = (currentMember.name ?? currentMember.email ?? '').split(/\s+/)[0];
    ariaLabel = `Assigned to ${currentMember.name ?? currentMember.email ?? 'a teammate'}`;
    triggerLabel = (
      <>
        <UserAvatar
          slackId={currentMember.slack_user_id}
          teamMembersBySlackId={teamMembersBySlackId}
          size="xs"
        />
        <span className={cn('max-w-[80px] truncate', labelCls)}>{firstName}</span>
      </>
    );
  } else {
    triggerLabel = (
      <>
        <UserPlus className="h-3.5 w-3.5" />
        <span className={labelCls}>Assign</span>
      </>
    );
  }

  // Nothing to pick from: show who holds it, without a dead dropdown.
  if (!showSelfRow && !canAssignOthers) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled
        aria-label={ariaLabel}
        title={access.isLoading ? ariaLabel : `${ariaLabel} (your role can't change the assignee here)`}
        className={compact ? 'max-sm:gap-1 max-sm:px-2' : undefined}
      >
        {triggerLabel}
      </Button>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={ariaLabel}
          title={ariaLabel}
          className={compact ? 'max-sm:gap-1 max-sm:px-2' : undefined}
        >
          {triggerLabel}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      {/* max-w caps the popover at ~viewport-width so it never pokes off
          the right edge on narrow phones (360px). */}
      <DropdownMenuContent align="start" className="w-72 max-w-[calc(100vw-1rem)] p-0">
        <div className="border-b border-border/60 p-2">
          <PickerSearch autoFocus value={search} onChange={setSearch} />
        </div>

        <div className="max-h-72 overflow-y-auto py-1">
          {/* Self row — pinned, so "I'm taking this" stays one click.
              Greyed-with-check when already mine so re-clicking is a
              no-op (handled in pick()) and the state is obvious. */}
          {selfMember && (showSelfRow || isMine) ? (
            <button
              type="button"
              onClick={() => pick(selfMember.slack_user_id)}
              // Assignment is single-select, so radio rather than checkbox
              // semantics — and the row announces the state the aria-hidden
              // check square only shows.
              role="menuitemradio"
              aria-checked={isMine}
              className={PICKER_ROW_CLASS}
            >
              <UserAvatar
                slackId={selfMember.slack_user_id}
                teamMembersBySlackId={teamMembersBySlackId}
                size="sm"
              />
              <div className="min-w-0 flex-1">
                {/* truncate so a long email-as-name doesn't push the
                    CheckMark off the right edge of the dropdown. */}
                <p className="truncate font-medium">
                  {selfMember.name ?? selfMember.email}{' '}
                  <span className="text-muted-foreground">(you)</span>
                </p>
                {!isMine ? (
                  <p className="truncate text-3xs text-muted-foreground">Claim this ticket</p>
                ) : null}
              </div>
              <PickerCheck on={isMine} />
            </button>
          ) : null}

          {selfMember && (showSelfRow || isMine) && canAssignOthers && others.length > 0 ? (
            <div className="my-1 border-t border-border/40" />
          ) : null}

          {!canAssignOthers ? null : others.length === 0 ? (
            <PickerEmpty searching={!!search.trim()} />
          ) : (
            others.map((m) => {
              const isCurrent = m.slack_user_id === currentAssigneeSlackId;
              return (
                <button
                  key={m.slack_user_id}
                  type="button"
                  onClick={() => pick(m.slack_user_id)}
                  role="menuitemradio"
                  aria-checked={isCurrent}
                  className={PICKER_ROW_CLASS}
                >
                  <UserAvatar
                    slackId={m.slack_user_id}
                    teamMembersBySlackId={teamMembersBySlackId}
                    size="sm"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{m.name ?? m.email}</p>
                    {m.name ? (
                      <p className="truncate text-3xs text-muted-foreground">
                        {m.email}
                      </p>
                    ) : null}
                  </div>
                  <PickerCheck on={isCurrent} />
                </button>
              );
            })
          )}
        </div>

        <div className="border-t border-border/60 px-2.5 py-1.5 text-2xs text-muted-foreground">
          {currentMember
            ? `Assigned to ${currentMember.name ?? currentMember.email}`
            : 'Unassigned'}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

